import { z } from 'zod'
import { TASTE_DETAIL_ACCESS, TASTE_TARGET_ACCESS } from '../taste-axes'

/** 구독 플랜. RevenueCat 엔타이틀먼트 id 와 같은 이름 (basic · pro), 없으면 free */
export const PLANS = ['free', 'basic', 'pro'] as const
export type Plan = (typeof PLANS)[number]
export const PAID_PLANS = ['basic', 'pro'] as const
export type PaidPlan = (typeof PAID_PLANS)[number]

export type PlanPeriod = 'monthly' | 'yearly'

/** RevenueCat 상품 id (App Store Connect / Play 에 같은 id 로 등록). 엔타이틀먼트 id 는 플랜 이름과 같다 */
export const PLAN_PRODUCTS = {
  basic: { monthly: 'basic_monthly', yearly: 'basic_yearly' },
  pro: { monthly: 'pro_monthly', yearly: 'pro_yearly' },
} as const

/**
 * 스토어에서 산 플랜 구독 상품 id 로 맞춘다. Play 는 `pro_monthly:monthly`(subscriptionId:basePlanId) 로 올 수 있어 콜론 앞만 본다.
 * 플랜 구독 상품이 아니면 null — RevenueCat 대시보드에서 준 프로모션(`rc_promo_pro_monthly` 등)이나 상품 없이 준 플랜.
 */
export function storePlanProductId(productId: string | null | undefined): string | null {
  const id = productId?.split(':')[0]
  if (!id) return null
  const ids: readonly string[] = Object.values(PLAN_PRODUCTS).flatMap((x) => [x.monthly, x.yearly])
  return ids.includes(id) ? id : null
}

/**
 * 구매 복원 결과 — 스토어 구매 기록(활성 구독 + 지난 구매 상품 id)에 플랜 구독이 하나라도 있는지.
 * 권한(entitlements)으로 보지 않는다: 심사 데모 계정은 프로모션 Pro(`rc_promo_…`)가 있어 권한만 보면 늘 '복원됨'이 된다.
 * 소모성(견적 크레딧·생성 팩)은 복원 대상이 아니라 세지 않는다
 */
export function hasStorePlanPurchase(productIds: readonly string[]): boolean {
  return productIds.some((id) => storePlanProductId(id) !== null)
}

/**
 * 생성 10회 팩 — 월 생성을 다 쓴 뒤 쓰는 독립 소모품. 판매 화면은 Pro 에게만 열지만(apps/mobile generation-pack.ts),
 * 산 팩에는 만료가 없고 구독이 끝나도 남은 횟수를 계속 쓴다(2026-10-30 대표 결정 2차 A, 애플 3.1.1 — 별도 구매는 구독 만료로
 * 취소하지 않는다). 매월 초기화는 구독에 포함된 월 사용량에만 해당한다. generation_packs.expires_at 은 쓰지 않는 칸이다
 */
export const GENERATION_PACK_PRODUCT_ID = 'generation_pack_10'
export const GENERATION_PACK_SIZE = 10

/**
 * 플랜별 한도 (2026-09-16 결정). 어드민 설정 planQuotas 에서 바꾼다.
 * 과금 단위는 "월 생성 횟수" — 삭제해도 그 달 카운트는 돌아오지 않고, 실패한 생성은 세지 않는다. 보관은 무제한.
 */
export const planQuotaSchema = z.object({
  /** 월 생성 횟수. free 는 평생 */
  generations: z.number().int().min(0).max(100000),
  /** 시뮬레이션 1건당 사진 수 (1 = plated 만) */
  photos: z.number().int().min(1).max(3),
  /** AI 수정 턴. free 는 제품당, 유료는 월 합산 */
  turns: z.number().int().min(0).max(100000),
  /** 사진 다시 생성 (월) */
  regenerations: z.number().int().min(0).max(100000),
  /** 수정본을 복사본으로 저장 */
  duplicate: z.boolean(),
  /** 판매 시장 직접 선택 (아니면 언어 기준 고정) */
  marketSelect: z.boolean(),
  /** 결과에 보여주는 경쟁 제품 수 */
  competitors: z.number().int().min(0).max(20),
  /** 매달 포함되는 공장 견적 크레딧 */
  includedQuotes: z.number().int().min(0).max(100),
  /** 공유 페이지·다운로드 사진에 RecipeCat 배지 */
  badge: z.boolean(),
  /**
   * 결과 화면 '제품 사양서' 저장 (업무 산출물, 2026-10-29). 이 칸보다 먼저 저장된 KV 값에는 없어서
   * 플랜마다 기본값을 채운다 — Free 는 꺼짐, Basic·Pro 는 켬 (withPlanQuotaDefaults 가 DEFAULT_PLAN_QUOTAS 에서)
   */
  specSheet: z.boolean().default(false),
  /** 맛 고르기 세부 축 (taste-axes.ts): preview = 흐린 미리보기, basic = 2~3축 3단계, full = 전부 5단계 */
  tasteDetail: z.enum(TASTE_DETAIL_ACCESS),
  /** 맛 고르기 수치 목표 (염도 %, 당도 Brix …): hidden = 안 보임, view = 보기만, edit = 직접 입력 */
  tasteTargets: z.enum(TASTE_TARGET_ACCESS),
})
export type PlanQuota = z.infer<typeof planQuotaSchema>

/**
 * 저장된 planQuotas(운영 KV, 옛 서버의 /config)에 나중에 더한 칸(specSheet·tasteDetail·tasteTargets …)이 없으면
 * 그 플랜의 기본값으로 채운다. 채우지 않으면 칸 하나 때문에 planQuotas 가 통째로 깨져 앱 설정 전체가 기본값으로 돌아간다
 */
function withPlanQuotaDefaults(raw: unknown): unknown {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return raw
  const quotas = raw as Record<string, unknown>
  const fill = (plan: Plan) => {
    const saved = quotas[plan]
    return saved && typeof saved === 'object' && !Array.isArray(saved)
      ? { ...DEFAULT_PLAN_QUOTAS[plan], ...saved }
      : saved
  }
  return { ...quotas, free: fill('free'), basic: fill('basic'), pro: fill('pro') }
}

export const planQuotasSchema = z.preprocess(
  withPlanQuotaDefaults,
  z.object({
    free: planQuotaSchema,
    basic: planQuotaSchema,
    pro: planQuotaSchema,
    /** 7일 체험 중 생성 캡 (기능은 전부 열리고 양만 제한) */
    trialGenerations: z.number().int().min(0).max(100000),
  }),
)
export type PlanQuotas = z.infer<typeof planQuotasSchema>

export const DEFAULT_PLAN_QUOTAS: PlanQuotas = {
  free: {
    generations: 2,
    photos: 1,
    turns: 3,
    regenerations: 0,
    duplicate: false,
    marketSelect: false,
    competitors: 0,
    includedQuotes: 0,
    badge: true,
    specSheet: false,
    tasteDetail: 'preview',
    tasteTargets: 'hidden',
  },
  basic: {
    generations: 15,
    photos: 3,
    turns: 100,
    regenerations: 0,
    duplicate: true,
    marketSelect: false,
    competitors: 1,
    includedQuotes: 0,
    badge: false,
    specSheet: true,
    tasteDetail: 'basic',
    tasteTargets: 'view',
  },
  pro: {
    generations: 60,
    photos: 3,
    turns: 500,
    regenerations: 30,
    duplicate: true,
    marketSelect: true,
    competitors: 5,
    includedQuotes: 1,
    badge: false,
    specSheet: true,
    tasteDetail: 'full',
    tasteTargets: 'edit',
  },
  trialGenerations: 10,
}

export type PeriodType = 'normal' | 'trial'

/** GET /me/plan — 현재 플랜과 이번 기간 사용량. 앱은 이걸로 잠금·배지·페이월을 그린다 */
export interface Entitlements {
  plan: Plan
  /** trial = 7일 체험 중 (연간 상품). free 는 null */
  periodType: PeriodType | null
  /** 이번 달 카운트 기간 (ISO). free 는 null (평생) */
  periodStart: string | null
  periodEnd: string | null
  /** 구독 상품 id (basic_yearly 등). free 는 null */
  productId: string | null
  quota: PlanQuota
  generations: {
    used: number
    /** 이번 기간 한도 (체험 중이면 trialGenerations) */
    limit: number
    /** 산 팩에 남은 횟수. 만료가 없고 플랜과 상관없이 쓴다 (2026-10-30 대표 결정 2차 A) */
    packRemaining: number
    /**
     * 지금 못 쓰는 팩 횟수. 산 팩은 잠기지 않아(대표 결정 2차 A) 지금 서버는 늘 0 이다.
     * 이 값을 읽는 앱(1.0.8 개발판)이 있어 칸은 남긴다. 옛 서버는 칸이 없다 — 앱은 없으면 0 으로 본다
     */
    packLocked?: number
  }
  /** free 는 제품당 카운트라 used 가 0 — 서버가 /edit 에서 제품별로 본다 */
  turns: { used: number; limit: number }
  regenerations: { used: number; limit: number }
  quoteCredits: {
    /** 아직 쓰지 않은 견적 크레딧 (구매 + 포함) */
    available: number
    /** 이 플랜의 견적 상품 id (앱이 결제창에 띄움) */
    productId: string
    /** false 면 서버에 RevenueCat 키가 없어 결제 없이 받는다 (로컬) */
    paymentsEnabled: boolean
  }
}

/** 한도·기능 잠금 응답의 error.code */
export const PLAN_LIMIT_CODE = 'PLAN_LIMIT'
export const TURN_LIMIT_CODE = 'TURN_LIMIT'
export const PLAN_FEATURE_CODE = 'PLAN_FEATURE'
export const GUEST_LIMIT_CODE = 'GUEST_LIMIT'
export const REGEN_LIMIT_CODE = 'REGEN_LIMIT'
export type PlanErrorCode =
  | typeof PLAN_LIMIT_CODE
  | typeof TURN_LIMIT_CODE
  | typeof PLAN_FEATURE_CODE
  | typeof GUEST_LIMIT_CODE
  | typeof REGEN_LIMIT_CODE

/** 잠긴 기능 이름 (PLAN_FEATURE 응답 · 페이월 진입점) */
export const PLAN_FEATURES = [
  'duplicate',
  'market_select',
  'compare',
  'regenerate',
  'photos',
  /** 결과 화면 '제품 사양서' 저장 (2026-10-29). 앱이 그리는 산출물이라 서버 경로는 없다 */
  'spec_sheet',
] as const
export type PlanFeature = (typeof PLAN_FEATURES)[number]

/** 이 플랜 한도로 기능을 쓸 수 있는지 — 서버(assertFeature)와 앱 잠금이 같은 판정식을 쓴다 */
export function planAllowsFeature(quota: PlanQuota, feature: PlanFeature): boolean {
  switch (feature) {
    case 'duplicate':
      return quota.duplicate
    case 'market_select':
      return quota.marketSelect
    case 'compare':
      return quota.competitors > 0
    case 'regenerate':
      return quota.regenerations > 0
    case 'photos':
      return quota.photos > 1
    case 'spec_sheet':
      // 옛 서버의 GET /me/plan 에는 칸이 없다 — 없으면 잠금으로 본다
      return quota.specSheet === true
  }
}

/** 페이월을 띄운 곳 (분석 · 헤드라인 · 기본 선택) */
export const PAYWALL_SOURCES = [
  'first_result',
  'photo_locked',
  'generation_limit',
  'guest_limit',
  'turn_limit',
  'duplicate',
  'market_select',
  'compare',
  'regenerate',
  'quote',
  /** 견적 결제 직전 Pro 비교 카드 (1.0.1). Pro 월간이 기본 선택 */
  'quote_compare',
  'me_tab',
  'home_badge',
  /** 결과 화면 상시 업셀 줄의 '플랜 비교' (2026-10-29) */
  'result_upsell',
  /** 남은 생성이 거의 없을 때 결과를 나가면 자동으로 (2026-10-29, 빈도 상한은 앱 result-upsell.ts) */
  'near_limit',
  /** 잠긴 '제품 사양서' 버튼 (2026-10-29) */
  'spec_sheet',
  /** 맛 고르기의 잠긴 세부 축·수치 목표 (quota.tasteDetail · tasteTargets) */
  'taste_detail',
] as const
export type PaywallSource = (typeof PAYWALL_SOURCES)[number]

/** 어드민 → 사용자 조회/관리 */
export const emailSchema = z.object({ email: z.string().trim().toLowerCase().email().max(120) })

export interface AdminUserView {
  userId: string
  email: string
  name: string
  plan: Plan
  periodType: PeriodType | null
  periodEnd: string | null
  /** 어드민 여부와 근거 */
  isAdmin: boolean
  adminSource: 'env' | 'clerk' | 'table' | null
  createdAt: string
}

export interface AdminEntry {
  email: string
  userId: string | null
  addedBy: string
  /** env(ADMIN_EMAILS) 항목은 어드민 화면에서 지울 수 없다 */
  source: 'env' | 'table'
  createdAt: string | null
}

/** 결과 화면의 경쟁 제품 (골든셋). Basic 은 1개 + 나머지 잠금, Pro 는 5개 */
export interface CompetitorView {
  id: string
  name: string
  brand: string
  retailPrice: number | null
  currency: 'KRW' | 'USD'
  packSize: string
  positioning: string
  sourceUrl: string | null
}
export interface CompetitorsView {
  items: CompetitorView[]
  /** 플랜 때문에 안 보여준 수 (0 이면 전부 보임) */
  locked: number
}

/** A store-confirmed purchase identifier, never a receipt/token. The server verifies ownership. */
export const pendingPlanPurchaseSchema = z
  .object({
    productId: z.enum([
      'basic_monthly',
      'basic_yearly',
      'pro_monthly',
      'pro_yearly',
      'generation_pack_10',
    ]),
    transactionId: z.string().min(1).max(200),
    store: z.enum(['app_store', 'play_store']),
  })
  .strict()
export type PendingPlanPurchase = z.infer<typeof pendingPlanPurchaseSchema>
export type PurchaseSyncState = 'pending' | 'applied' | 'settled'
